import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { displayName, DEFAULT_ISPETTORATO_FIELDS } from "../lib/anagConfig";
import { ChevronsUpDown, Search, Plus, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import PraticaSummaryChips from "./PraticaSummaryChips";
import { IspettoratoForm, ISP_COLS, AGE_COLS, buildRow, ispPool } from "./CompagniaSezioni";

const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
const KINDS = {
  ispettorati: { cols: ISP_COLS, one: "ispettorato", sede: true, wa: "ispettorato" },
  agenzie: { cols: AGE_COLS, one: "agenzia", sede: false, wa: "agenzia" },
};
const label = (i) => (typeof i === "string" ? i : [i?.codice_agenzia, i?.denominazione || i?.nome].filter(Boolean).join(" - ") || "").trim();
const matchName = (o, v) => o && typeof o === "object" && (norm(label(o)) === norm(v) || norm(o.denominazione || o.nome) === norm(v));

// Dropdown ispettorati filtrato per compagnia: mostra SOLO gli ispettorati della compagnia
// selezionata, collegata tramite ID univoco (non per nome). Consente di creare al volo un nuovo
// ispettorato e collegarlo alla compagnia selezionata, senza passare dall'anagrafica.
export default function IspettoratoSelect({ compagnia, value, onChange, testId = "ispettorato-select", kind = "ispettorati" }) {
  const K = KINDS[kind];
  const EMPTY = Object.fromEntries(K.cols.map((c) => [c.key, ""]));
  const [comps, setComps] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editOrig, setEditOrig] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const ref = useRef(null);

  const loadComps = () => api.get("/anagrafiche/compagnie").then(({ data }) => { setComps(data || []); return data || []; }).catch(() => { setComps((c) => c || []); return null; });
  useEffect(() => {
    loadComps();
    const h = () => loadComps();
    window.addEventListener("pgp:compagnie-updated", h);
    return () => window.removeEventListener("pgp:compagnie-updated", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const { nameToId, byId } = useMemo(() => {
    const n2i = {}, bi = {};
    (comps || []).forEach((c) => {
      if (!c.id) return;
      bi[c.id] = c;
      [displayName(c), c.denominazione, c.ragione_sociale, c.nome, c.compagnia, c.codice_ivass, c.codice_ania].forEach((x) => {
        const k = norm(x);
        if (k && !(k in n2i)) n2i[k] = c.id;
      });
    });
    return { nameToId: n2i, byId: bi };
  }, [comps]);

  const targetComp = compagnia ? byId[nameToId[norm(compagnia)]] : null;

  const options = useMemo(() => {
    const list = (targetComp?.[kind] || []).map(label).filter(Boolean);
    const uniq = [...new Set(list)].sort((a, b) => a.localeCompare(b, "it", { sensitivity: "base" }));
    const base = compagnia && K.sede ? ["Sede Legale", ...uniq] : uniq;
    if (value && !base.some((n) => norm(n) === norm(value))) base.unshift(value);
    return base;
  }, [targetComp, value, compagnia, kind, K.sede]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((n) => n.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const disabled = !compagnia;
  const placeholder = disabled ? "Seleziona prima la compagnia" : `Seleziona ${K.one}…`;

  // Oggetto ispettorato selezionato (per riepilogo recapiti e apertura scheda). "Sede Legale"
  // è un'opzione sintetica senza record: nessun riepilogo/scheda.
  const selObj = useMemo(
    () => (targetComp?.[kind] || []).find((o) => matchName(o, value)) || null,
    [targetComp, value, kind]
  );

  const select = (n) => { onChange(n); setOpen(false); setQ(""); };

  const openAdd = () => {
    setForm({ ...EMPTY, denominazione: q.trim() });
    setEditMode(false); setEditOrig("");
    setAddOpen(true);
    setOpen(false);
  };

  const openEdit = async () => {
    if (!selObj) return;
    const fresh = await loadComps();
    const fc = (fresh || []).find((c) => c.id === targetComp?.id);
    const row = (fc?.[kind] || []).find((o) => (selObj.id && o?.id === selObj.id) || matchName(o, value)) || selObj;
    setForm({ ...EMPTY, ...row });
    setEditMode(true); setEditOrig(row.id || label(row));
    setAddOpen(true); setOpen(false);
  };

  const pool = useMemo(() => ispPool(comps), [comps]);

  const saveNew = async () => {
    const nome = (form.denominazione || "").trim();
    if (!nome) { toast.error(`Inserisci il nome ${kind === "agenzie" ? "dell'agenzia" : "dell'ispettorato"}`); return; }
    if (!targetComp?.id) { toast.error("Compagnia non trovata in anagrafica"); return; }
    setSaving(true);
    try {
      const base = buildRow(form, K.cols);
      const { data: fresh } = await api.get("/anagrafiche/compagnie");
      const freshComp = (fresh || []).find((c) => c.id === targetComp.id) || targetComp;
      const current = Array.isArray(freshComp[kind]) ? freshComp[kind] : [];
      let updated;
      if (editMode) {
        updated = current.map((o) => {
          if (!(o && typeof o === "object" && (o.id ? o.id === editOrig : false)) && !matchName(o, editOrig)) return o;
          const { telefono_extra, email_extra, pec_extra, ...rest } = o;
          return { ...rest, ...base };
        });
      } else {
        updated = [...current, base];
      }
      await api.patch(`/anagrafiche/compagnie/${targetComp.id}`, { [kind]: updated });
      setComps((fresh || []).map((c) => (c.id === targetComp.id ? { ...c, [kind]: updated } : c)));
      window.dispatchEvent(new Event("pgp:compagnie-updated"));
      toast.success(editMode ? `Scheda ${K.one} aggiornata` : `${K.one[0].toUpperCase()}${K.one.slice(1)} aggiunto e collegato alla compagnia`);
      setAddOpen(false);
      onChange(label(base));
    } catch (err) {
      toast.error(`Errore nel salvataggio ${kind === "agenzie" ? "dell'agenzia" : "dell'ispettorato"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1.5">
      <button type="button" disabled={disabled} data-testid={testId}
        onClick={() => { if (!disabled) { setOpen((o) => !o); setQ(""); } }}
        className={`h-9 w-full flex items-center justify-between gap-2 rounded-md border px-3 text-sm text-left transition-colors ${
          disabled ? "bg-slate-900/30 border-slate-800 cursor-not-allowed" : "bg-slate-900/60 border-slate-700 hover:border-amber-500/40"
        }`}>
        <span className={`truncate ${value ? "text-slate-100" : "text-slate-500"}`}>{value || placeholder}</span>
        <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0" />
      </button>
      {selObj && kind !== "agenzie" && (
        <button type="button" onClick={openEdit} data-testid={`${testId}-edit`} title={`Apri scheda ${K.one}`}
          className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-teal-500/40 text-teal-600 hover:bg-teal-500/10"><Pencil className="h-4 w-4" /></button>
      )}
      </div>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-xl" data-testid={`${testId}-list`}>
          <div className="relative p-2 border-b border-slate-800">
            <Search className="h-3.5 w-3.5 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ispettorato…" data-testid={`${testId}-search`}
              className="h-8 w-full rounded bg-slate-900/60 border border-slate-700 text-slate-100 text-sm pl-7 pr-2 focus:outline-none focus:ring-1 focus:ring-amber-500/40" />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {value && (
              <button type="button" onClick={() => select("")} data-testid={`${testId}-clear`}
                className="w-full text-left px-3 py-2 text-sm text-slate-400 hover:bg-slate-800/60 border-b border-slate-800">— Nessuno —</button>
            )}
            {options.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-500">Nessun{kind === "agenzie" ? "a agenzia" : " ispettorato"} per questa compagnia.</div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-500">Nessun risultato per “{q}”.</div>
            ) : filtered.map((n) => (
              <button key={n} type="button" onClick={() => select(n)} data-testid={`${testId}-opt`}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800/60 ${norm(n) === norm(value) ? "text-amber-300" : "text-slate-200"}`}>{n}</button>
            ))}
          </div>
          <button type="button" onClick={openAdd} data-testid={`${testId}-add`}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-teal-300 hover:bg-teal-500/10 border-t border-slate-800 transition-colors">
            <Plus className="h-4 w-4" /> Aggiungi {kind === "agenzie" ? "nuova agenzia" : "nuovo ispettorato"}
          </button>
        </div>
      )}

      {selObj && (
        <div className="mt-1 flex flex-wrap items-center gap-2" data-testid={`${testId}-info`}>
          {kind === "agenzie" && (
            <button type="button" onClick={openEdit} data-testid={`${testId}-edit`} title={`Apri scheda ${K.one}`}
              className="text-slate-500 hover:text-amber-600 transition-colors shrink-0"><Pencil className="h-3.5 w-3.5" /></button>
          )}
          <PraticaSummaryChips rec={selObj} testId={testId} defaultKeys={DEFAULT_ISPETTORATO_FIELDS} waCategory={K.wa} />
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={(o) => { if (!saving) setAddOpen(o); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0f1420] text-slate-100 border border-slate-800" data-testid={`${testId}-add-dialog`}>
          <DialogHeader>
            <DialogTitle>{editMode ? `Scheda ${K.one}` : (kind === "agenzie" ? "Nuova agenzia" : "Nuovo ispettorato")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm">
              <span className="text-slate-400">Compagnia collegata: </span>
              <span className="font-medium" data-testid={`${testId}-add-compagnia`}>{(targetComp && displayName(targetComp)) || compagnia || "—"}</span>
            </div>
            <IspettoratoForm row={form} setRow={(patch) => setForm((f) => ({ ...f, ...patch }))} testId={`${testId}-isp`} pool={pool} cols={K.cols} noteKey={kind === "agenzie" ? "age" : "isp"} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving} data-testid={`${testId}-add-cancel`}>Annulla</Button>
            <Button onClick={saveNew} disabled={saving} data-testid={`${testId}-add-save`}>{saving ? "Salvataggio…" : "Salva e seleziona"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
